import dayjs from "dayjs";
import { ITEM_TYPE } from "../Enums/Enum";

// ==============================
// Payroll Entry Payload Builders
// ==============================

// --- Helper Functions ---

const formatDate = (value) => {
  if (!value) return null;
  return dayjs(value).format("YYYY-MM-DD");
};

const buildFiltersPayload = (filters) => {
  return (filters || []).reduce((acc, filter) => {
    const columnType = Number(filter?.columnType ?? filter?.ColumnType);
    const operator = filter?.operator ?? filter?.Operator;
    const value = filter?.value ?? filter?.Value;

    if (!columnType || !operator) return acc;

    // Special case for Status (ColumnType = 1)
    if (columnType === 1) {
      acc.push({
        ColumnType: columnType,
        Operator: operator,
        Value: value ?? "",
      });
      return acc;
    }

    if (!value || String(value).trim() === "") return acc;

    acc.push({
      ColumnType: columnType,
      Operator: operator,
      Value: String(value),
    });

    return acc;
  }, []);
};

const parseHoursToDecimal = (value) => {
  if (!value) return 0;
  const strVal = String(value).trim();
  if (!strVal.includes(":")) return parseFloat(strVal) || 0;
  const [h, m] = strVal.split(":");
  const hours = parseInt(h, 10) || 0;
  const minutes = parseInt(m, 10) || 0;
  const isNegative = hours < 0 || (hours === 0 && strVal.startsWith("-"));
  const decimal = Math.abs(hours) + minutes / 60;
  return isNegative ? -decimal : decimal;
};

// ==============================
// View Grid Payload
// ==============================
export const buildViewGridPayload = (
  selectedCorp,
  fromDate,
  toDate,
  sortOrder,
  page,
  sortBy,
  searchFilters,
  status,
  STATUS_OPERATOR_MAP,
  STATUS_VALUE_MAP,
) => {
  const activeFilters = [...(searchFilters || [])];
  if (
    status &&
    status !== "--All--" &&
    STATUS_OPERATOR_MAP[status] &&
    STATUS_VALUE_MAP[status]
  ) {
    activeFilters.push({
      columnType: 1,
      operator: STATUS_OPERATOR_MAP[status],
      value: STATUS_VALUE_MAP[status],
    });
  }

  return {
    CorpID:
      selectedCorp?.corpValue && selectedCorp.corpValue.trim() !== ""
        ? selectedCorp.corpValue
        : null,
    FromDate: formatDate(fromDate),
    ToDate: formatDate(toDate),
    SortOrder: sortOrder ? true : false,
    PageNumber: page + 1,
    SortBy: Number(sortBy) || 105,
    Filters: buildFiltersPayload(activeFilters),
  };
};

// ==============================
// Update Pay Period Setup Payload
// ==============================
export const buildUpdatePayPeriodSetupPayload = (
  values,
  payrollId,
  corporationId,
  frequencyOptions,
  accountOptions,
  isPcUpdate = false,
  profitCenterOptions = [],
) => {
  const selectedFreq = frequencyOptions.find(
    (opt) => opt.value === values.frequency || opt.label === values.frequency,
  );
  const selectedAccount = accountOptions.find(
    (opt) => opt.value === values.account || opt.label === values.account,
  );
  const selectedPc = (profitCenterOptions || []).find(
    (opt) =>
      opt.value === values.profitCenter ||
      opt.label === values.profitCenter ||
      opt.label === values.profitCenterName ||
      opt.id === values.profitCenterId ||
      opt.id === values.profitCenter,
  );

  const pcUpdateFlag = typeof isPcUpdate === "boolean"
    ? isPcUpdate
    : (typeof values?.isPcUpdate === "boolean" ? values.isPcUpdate : false);

  const pcId = selectedPc?.id || values.profitCenterId || (values.profitCenter && values.profitCenter !== selectedPc?.label ? values.profitCenter : null);

  return {
    PayrollID: payrollId ? payrollId : values?.payRollID,
    CorporationId: corporationId,
    CorporationName: values.corporation?.corpName || values.corporation,
    FrequencyID: selectedFreq?.id || "",
    PayFrequency: values.frequency,
    PayPeriodFrom: values.payFrom
      ? dayjs(values.payFrom).format("YYYY-MM-DD")
      : null,
    PayPeriodTo: values.payTo ? dayjs(values.payTo).format("YYYY-MM-DD") : null,
    PayDate: values.payDate ? dayjs(values.payDate).format("YYYY-MM-DD") : null,
    PayrollName: values.payrollName,
    AccountId: selectedAccount?.value || "",
    Account: selectedAccount?.label || "",
    IsAccrual: values.isAccrual ? 1 : 0,
    ImportPayrollType: values?.importPayrollType || 1,
    PCID: pcId || null,
    PCName: selectedPc?.label || values.profitCenterName || values.profitCenter || null,
    IsPcUpdate: pcUpdateFlag,
  };
};

// ==============================
// Update Payroll Employee Mapping Payload
// ==============================
export const buildUpdatePayrollEmployeeMappingPayload = (
  formValues,
  values,
  payrollId,
  corporationId,
  row,
  isVoid,
  voidDate,
  comments,
  empPayrollDetails,
  voidDatePref,
  employeeTotalGross,
  payPeriodTotals
) => {
  const empId = row?.raw?.PayrollEmpID || row?.raw?.PayrollEmpId;
  

  const wageItems = (values.wage || [])
    .map((item) => {
      const amt = Number(item.amount) || Number(item.Amount) || Number(item.gross) || 0;
      return {
        PayrollDetailID: typeof item.id === "string" ? item.id : "",
        TargetID: item.mappedTo || "",
        Type: ITEM_TYPE.Wages,
        ImportedItemName: item.wageName || "",
        ItemName: item.mappedToName || "",
        COA: item.coa || "",
        ExpenseCOA: "",
        Hours: Number(parseHoursToDecimal(item.hrs).toFixed(2)),
        Rate: Number(item.rate) || 0,
        GrossPay: 0,
        Amount: amt,
        TotalAmount: employeeTotalGross,
      };
    })
    .filter(item => item.Amount !== 0);

  // console.log("wageItems", values.wage)
  const addAndDeductionItems = (values.addAndDeductions || [])
    .filter((item) => item.addDeduction)
    .map((item) => {
      const type = Number(item.addDeduction);
      const parsedAmount = Number(item.amount) || 0;

      return {
        PayrollDetailID: typeof item.id === "string" ? item.id : "",
        TargetID: item.mappedTo || "",
        Type: type,
        ImportedItemName: item.item || "",
        ItemName: item.mappedToName || "",
        COA: item.coa || "",
        ExpenseCOA: "",
        Hours: 0,
        Rate: 0,
        GrossPay: 0,
          Amount: parsedAmount,
          TotalAmount: type === ITEM_TYPE.Deductions ? (payPeriodTotals["totalDeductions"] || 0) : (payPeriodTotals["totalAdditions"] || 0),
      };
    })
    .filter(item => item.Amount !== 0);

  // console.log("addAndDeductionItems", values.addAndDeductions)
  const taxItems = (values.eeAndErTaxes || [])
    .filter((item) => item.taxType)
    .map((item) => {
      const type = Number(item.taxType);
      return {
        PayrollDetailID: typeof item.id === "string" ? item.id : "",
        TargetID: item.mappedTo || "",
        Type: type,
        ImportedItemName: item.item || "",
        ItemName: item.mappedToName || "",
        COA: item.coa || "",
        ExpenseCOA: type === ITEM_TYPE.EmployerTaxes
          ? (item.expenseCoa || item.ExpenseCOA || item.LiabilityAccountID || item.ExpenseAccountID || item.PayableCOA || item.coa || "")
          : "",
        Hours: 0,
        Rate: 0,
        GrossPay: 0,
        Amount: Number(item.amount) || 0,
        // TotalAmount: type === 11 ? totalEETaxes : totalERTaxes,
        TotalAmount: payPeriodTotals[type === ITEM_TYPE.EmployeeTaxes ? "totalEETaxes" : "totalERTaxes"] || 0,
      };
    })
    .filter(item => item.Amount !== 0);

  const calculatedNetPay = Number(
    (
      (employeeTotalGross || 0) +
      (payPeriodTotals?.totalAdditions || 0) -
      (payPeriodTotals?.totalDeductions || 0) -
      (payPeriodTotals?.totalEETaxes || 0)
    ).toFixed(2)
  );

  const existingNetPay = (empPayrollDetails || []).find((item) => item.ItemType === 14);

  const netPayItem = {
    PayrollDetailID: existingNetPay?.PayrollDetailID || existingNetPay?.Id || "",
    TargetID: "",
    Type: ITEM_TYPE.NetPay,
    ImportedItemName: existingNetPay?.ImportedItemName || "NetPay",
    ItemName: "Net Pay",
    COA: values?.account || "",
    ExpenseCOA: "",
    Hours: 0,
    Rate: 0,
    GrossPay: 0,
    Amount: calculatedNetPay || 0,
    TotalAmount: calculatedNetPay || 0,
  };

  const payrollItemTypes = [...wageItems, ...addAndDeductionItems, ...taxItems, netPayItem];

  return {
    PayrollID: payrollId ? payrollId : values?.payRollID,
    PayrollEmpId: empId,
    EmployeeId:
      formValues.employeeId !== undefined
        ? formValues.employeeId || null
        : row?.raw?.EmployeeID || null,
    Corporation: corporationId,
    PayPeriodFrom: values.payFrom
      ? dayjs(values.payFrom).format("YYYY-MM-DD")
      : null,
    PayPeriodTo: values.payTo ? dayjs(values.payTo).format("YYYY-MM-DD") : null,
    EmployeeName: formValues.name,
    Department: formValues.department,
    JobTitle: formValues.job,
    PCID: formValues.profitCenter || null,
    ChequeNum: formValues.checkNo,
    ImportPayrollType: values?.importPayrollType || 1,
    ImportedNetPayAmount: parseFloat(String(formValues.importedNetPay || "0").replace(/,/g, "")) || 0,
    ImportedEmployeeName: formValues.importedName || "",
    ImportedDepartment: formValues.importedDept || "",
    ImportedJob: formValues.importedJob || "",
    IsApply: true,
    NetPayAmount: calculatedNetPay || 0,
    IsActive: 1,
    PayDate: (formValues?.payDate || values?.payDate) ? dayjs(formValues?.payDate || values?.payDate).format("YYYY-MM-DD") : null,
    IsVoid: isVoid ? 1 : 0,
    IsAccrual: !!values.isAccrual,
    VoidDate: isVoid && voidDate ? dayjs(voidDate).format("YYYY-MM-DD") : null,
    VoidReason: isVoid && comments ? comments : null,
    IsVoidDateEnabled: voidDatePref ? 1 : 0,
    IsLocked: row?.raw?.IsLocked || false,
    PayrollEmployeeDetailsID: row?.raw?.PayrollEmployeeDetailsID || null,
    // ChequeAmt: parseFloat(String(formValues.netPay).replace(/,/g, "")) || 0,
    ChequeAmt: 0,
    ClearedDate: row?.raw?.ClearedDate
      ? dayjs(row.raw.ClearedDate).format("YYYY-MM-DD")
      : null,
    PayrollItemTypes: payrollItemTypes,
  };
};

// ==============================
// Update Void Date Payload
// ==============================
export const buildUpdateVoidDatePayload = (
  payrollEmpId,
  row,
  isVoid,
  voidDate,
  comments,
  isVoidDateEnabled,
) => {
  const empId = payrollEmpId || row?.raw?.PayrollEmpID || row?.raw?.PayrollEmpId;
  const payload = {
    PayrollEmpID: empId,
    isVoid: isVoid,
    IsVoidDateEnabled: isVoidDateEnabled ?? false,
    VoidDate: isVoid && voidDate ? dayjs(voidDate).format("YYYY-MM-DD") : null,
  };

  if (isVoid) {
    if (comments !== undefined && comments !== null)
      payload.VoidRemarks = comments;
  }

  return payload;
};

// ==============================
// Save Payroll Employee Payload
// ==============================
export const buildSavePayrollEmployeePayload = (
  payrollId,
  employees = []
) => {
  return {
    PayrollID: String(payrollId || ""),
    Employees: (employees || []).map((emp) => ({
      EmployeeID: String(emp.EmployeeID || emp.EmployeeId || ""),
      IncDeptID: String(emp.IncDeptID || emp.IncDeptId || ""),
      JobID: String(emp.JobID || emp.JobId || ""),
      PCID: String(emp.PCID || emp.Pcid || ""),
    })),
  };
};
